import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { clsx } from "clsx";
import {
  ChevronDown,
  LayoutDashboard,
  Settings,
  Users,
  Bot,
  User,
  LogOut,
  Logs,
  Building2,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { useUserApplications } from "@/hooks/useUserApplications";
import { useAuthStore } from "@/stores/authStore";
import fissionLogo from "@/public/fission_logo_1.png";

interface SidebarProps {
  collapsed: boolean;
  onToggleSidebar: () => void;
}

const navLinkClass = (active: boolean, collapsed: boolean) =>
  clsx(
    "flex items-center text-md font-medium rounded-lg transition-colors",
    active
      ? "bg-white/20 text-white shadow-sm"
      : "text-white/70 hover:bg-white/10 hover:text-white",
    collapsed ? "justify-center p-2.5" : "gap-3 px-3 py-2.5"
  );

export function Sidebar({ collapsed, onToggleSidebar }: SidebarProps) {
  const location = useLocation();
  const { applications, loading } = useUserApplications();
  const { user, signOut, permissions } = useAuthStore();
  const [settingsOpen, setSettingsOpen] = useState(
    location.pathname.startsWith("/settings")
  );

  const normalizedPermissions = new Set(
    (permissions || []).map((permission) =>
      permission.toLowerCase().replace(/[\s_-]/g, "")
    )
  );
  const settingsAllowedGroups = ["admin", "superadmin"];
  const canSeeSettings = settingsAllowedGroups.some((group) =>
    normalizedPermissions.has(group.toLowerCase().replace(/[\s_-]/g, ""))
  );

  useEffect(() => {
    if (location.pathname.startsWith("/settings")) {
      setSettingsOpen(true);
    }
  }, [location.pathname]);



  const settingsSubLinks = [
    { to: "/settings/ai-apps", label: "AI Apps", icon: Bot },
    { to: "/settings/users", label: "Users", icon: Users },
    { to: "/settings/organizations", label: "Organizations", icon: Building2 },
    { to: "/settings/audit-logs", label: "Audit Logs", icon: Logs },
  ] as const;

  return (
    <aside
      className={clsx(
        "flex flex-col border-r gradient-bg shadow-lg transition-all duration-300 ease-in-out overflow-hidden",
        collapsed ? "w-[72px]" : "w-64"
      )}
    >
      {/* Header */}
      <div
        className={clsx(
          "flex items-center border-b border-white/10 px-3 py-3",
          collapsed ? "justify-center" : "justify-between"
        )}
      >

        <Link to="/dashboard" className="flex items-center gap-2 overflow-hidden">
          <img
            src={fissionLogo}
            alt="Fission AI Portal logo"
            className="h-7 w-7 shrink-0 rounded-md object-contain"
          />
          {!collapsed ? (
            <span className="truncate text-md font-semibold text-white">
              Fission AI Portal
            </span>
          ) : null}
        </Link>


        <button
          onClick={onToggleSidebar}
          className="p-1.5 rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition-colors"
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? (
            <ChevronsRight className="h-4 w-4" />
          ) : (
            <ChevronsLeft className="h-4 w-4" />
          )}
        </button>
      </div>

      {/* Navigation */}
      <div className="flex-1 overflow-y-auto py-4">
        <nav className="space-y-1 px-3">
          <Link
            to="/dashboard"
            className={navLinkClass(location.pathname === "/dashboard", collapsed)}
            title="Dashboard"
          >
            <LayoutDashboard className="h-5 w-5 shrink-0" />
            {!collapsed ? <span className="whitespace-nowrap">Dashboard</span> : null}
          </Link>

          {/* Applications */}
          <div className="pt-4">
            {!collapsed ? (
              <p className="px-3 text-sm font-bold text-white/70 uppercase tracking-wider whitespace-nowrap">
                Applications
              </p>
            ) : (
              <div className="mx-auto mb-1 h-px w-6 bg-white/20" />
            )}
            <div className="mt-2 space-y-1">
              {applications.map((app) => (
                <Link
                  key={app.id}
                  to={app.route}
                  className={navLinkClass(
                    location.pathname.startsWith(app.route),
                    collapsed
                  )}
                  title={app.appName}
                >
                  {app.icon ? (
                    <img
                      src={app.icon}
                      alt={`${app.appName} logo`}
                      className="h-5 w-5 shrink-0 rounded object-cover"
                    />
                  ) : (
                    <span className="h-2 w-2 rounded-full bg-current shrink-0" />
                  )}
                  {!collapsed ? (
                    <span className="whitespace-nowrap">{app.appName}</span>
                  ) : null}
                </Link>
              ))}
              {!collapsed && !loading && applications.length === 0 ? (
                <p className="px-3 py-2 text-xs text-white/60">No applications</p>
              ) : null}
            </div>
          </div>

          {/* Settings */}
          {canSeeSettings ? (
            <div className="pt-4 relative">
              <button
                type="button"
                onClick={() => {

                  setSettingsOpen((prev) => !prev);
                }}
                className={clsx(
                  "flex w-full items-center text-md font-medium rounded-lg transition-colors",
                  location.pathname.startsWith("/settings")
                    ? "bg-white/20 text-white shadow-sm"
                    : "text-white/70 hover:bg-white/10 hover:text-white",
                  collapsed
                    ? "justify-center p-2.5"
                    : "justify-between gap-3 px-3 py-2.5"
                )}
                title="Settings"
              >
                <span className={clsx("flex items-center", !collapsed && "gap-3")}>
                  <Settings className="h-5 w-5 shrink-0" />
                  {!collapsed ? (
                    <span className="whitespace-nowrap">Settings</span>
                  ) : null}
                </span>
                {!collapsed ? (
                  <ChevronDown
                    className={clsx(
                      "h-4 w-4 shrink-0 transition-transform",
                      settingsOpen && "rotate-180"
                    )}
                  />
                ) : null}
              </button>

              {/* Expanded: inline submenu */}
              {settingsOpen ? (
                <div className={clsx("mt-1 space-y-1 ", collapsed ? "pl-1" : "pl-10")}>
                  {settingsSubLinks.map(({ to, label, icon: Icon }) => (
                    <Link
                      key={to}
                      to={to}
                      className={clsx(
                        "flex items-center gap-2 rounded-md px-2 py-2 text-md transition-colors",
                        location.pathname === to
                          ? "bg-white/20 text-white"
                          : "text-white/70 hover:bg-white/10 hover:text-white",
                        collapsed
                          ? "justify-center p-2.5"
                          : "gap-3 px-3 py-2.5"
                      )}
                    >
                      <Icon className="h-4 w-4" />
                      {collapsed ? null : <span className="whitespace-nowrap">{label}</span>}
                    </Link>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
        </nav>
      </div>

      {/* Footer */}
      <div className="border-t border-white/10 px-3 py-3">
        <div
          className={clsx(
            "flex items-center",
            collapsed ? "flex-col gap-2" : "justify-between gap-2"
          )}
        >
          {!collapsed ? (
            <div className="min-w-0 text-white/80">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 shrink-0" />
                <span className="truncate text-md">
                  {user?.displayName || user?.username || user?.email || "User"}
                </span>
              </div>
            </div>
          ) : null}
          <button
            onClick={signOut}
            className={clsx(
              "inline-flex items-center gap-1.5 rounded-lg text-md text-white/70 hover:text-white hover:bg-white/10 transition-colors",
              collapsed ? "p-2.5 justify-center" : "px-2 py-1.5"
            )}
            title="Sign Out"
          >
            <LogOut className="h-4 w-4" />
            {!collapsed ? <span>Logout</span> : null}
          </button>
        </div>
      </div>
    </aside>
  );
}
